import React from "react";
import { Tabs, Descriptions, Tag } from "antd";
import { Space, SpaceType } from "../types/space";

const { TabPane } = Tabs;

interface SpaceDetailProps {
  selectedSpace: Space | null;
  treeData: any[];
}

const SpaceDetail: React.FC<SpaceDetailProps> = ({
  selectedSpace,
  treeData: _treeData, // 未使用，但保留以保持接口兼容性
}) => {
  // 获取空间类型的中文显示
  const getSpaceTypeDisplay = (type: SpaceType) => {
    switch (type) {
      case SpaceType.BUILDING:
        return "建筑";
      case SpaceType.ZONE:
        return "区域";
      case SpaceType.SPACE:
        return "空间";
      default:
        return type;
    }
  };

  // 获取空间状态的中文显示
  const getSpaceStatusDisplay = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "有效";
      case "INACTIVE":
        return "无效";
      case "MAINTENANCE":
        return "维护中";
      default:
        return status;
    }
  };

  // 渲染空状态 - 不显示任何内容
  if (!selectedSpace) {
    return null;
  }

  return (
    <Tabs defaultActiveKey="basic" type="card">
      <TabPane tab="基本信息" key="basic">
        <Descriptions
          bordered
          column={1}
          size="middle"
          labelStyle={{ width: "120px", fontWeight: "bold" }}
        >
          <Descriptions.Item label="空间ID">
            {selectedSpace.id}
          </Descriptions.Item>
          <Descriptions.Item label="名称">
            {selectedSpace.name}
          </Descriptions.Item>

          <Descriptions.Item label="类型">
            {getSpaceTypeDisplay(selectedSpace.type)}
          </Descriptions.Item>
          <Descriptions.Item label="状态">
            <span
              style={{
                color:
                  selectedSpace.status === "ACTIVE"
                    ? "#52c41a"
                    : selectedSpace.status === "INACTIVE"
                      ? "#f5222d"
                      : "#faad14",
              }}
            >
              {getSpaceStatusDisplay(selectedSpace.status)}
            </span>
          </Descriptions.Item>
          <Descriptions.Item label="描述">
            {selectedSpace.description || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="标签">
            {selectedSpace.tags && selectedSpace.tags.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {selectedSpace.tags.map((tag, index) => (
                  <Tag
                    key={tag.id || index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                      margin: "2px 0",
                    }}
                  >
                    {tag.icon && (
                      <div
                        style={{
                          width: 14,
                          height: 14,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                        dangerouslySetInnerHTML={{ __html: tag.icon }}
                      />
                    )}
                    <span>{tag.name}</span>
                  </Tag>
                ))}
              </div>
            ) : (
              <span style={{ color: "#999" }}>-</span>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="创建时间">
            {new Date(selectedSpace.createdAt).toLocaleString("zh-CN")}
          </Descriptions.Item>
          <Descriptions.Item label="更新时间">
            {new Date(selectedSpace.updatedAt).toLocaleString("zh-CN")}
          </Descriptions.Item>
        </Descriptions>
      </TabPane>

      <TabPane tab="坐标信息" key="coordinate">
        <Descriptions
          bordered
          column={1}
          size="middle"
          labelStyle={{ width: "120px", fontWeight: "bold" }}
        >
          <Descriptions.Item label="坐标信息">
            {selectedSpace.coordinates && selectedSpace.coordinates.length > 0
              ? `${selectedSpace.coordinates.length} 个坐标系统`
              : "未设置"}
          </Descriptions.Item>
          <Descriptions.Item label="坐标类型">
            {selectedSpace.coordinates && selectedSpace.coordinates.length > 0
              ? selectedSpace.coordinates[0].coordinateType
              : "未设置"}
          </Descriptions.Item>
          <Descriptions.Item label="中心点">
            {selectedSpace.coordinates && selectedSpace.coordinates.length > 0
              ? JSON.stringify(selectedSpace.coordinates[0].center)
              : "未设置"}
          </Descriptions.Item>
          <Descriptions.Item label="边界">
            {selectedSpace.coordinates && selectedSpace.coordinates.length > 0
              ? JSON.stringify(selectedSpace.coordinates[0].boundary).substring(
                  0,
                  0
                ) + "..."
              : "未设置"}
          </Descriptions.Item>
        </Descriptions>
      </TabPane>

      <TabPane tab="图纸模型" key="drawings">
        <Descriptions
          bordered
          column={1}
          size="middle"
          labelStyle={{ width: "120px", fontWeight: "bold" }}
        >
          <Descriptions.Item label="图纸数量">
            {selectedSpace.drawings && selectedSpace.drawings.length > 0
              ? `${selectedSpace.drawings.length} 个图纸`
              : "未设置"}
          </Descriptions.Item>
          <Descriptions.Item label="图纸类型">
            {selectedSpace.drawings && selectedSpace.drawings.length > 0
              ? Array.from(
                  new Set(selectedSpace.drawings.map((d) => d.drawingType))
                ).join(", ")
              : "未设置"}
          </Descriptions.Item>
          <Descriptions.Item label="当前版本">
            {selectedSpace.drawings && selectedSpace.drawings.length > 0
              ? selectedSpace.drawings.find((d) => d.isActive)?.version ||
                "未设置"
              : "未设置"}
          </Descriptions.Item>
        </Descriptions>
      </TabPane>

      <TabPane tab="层级关系" key="hierarchy">
        <Descriptions
          bordered
          column={1}
          size="middle"
          labelStyle={{ width: "120px", fontWeight: "bold" }}
        >
          <Descriptions.Item label="空间路径">
            {selectedSpace.spacePath || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="空间层级">
            {getSpaceTypeDisplay(selectedSpace.type)}
          </Descriptions.Item>
          <Descriptions.Item label="子空间数量">
            {selectedSpace._count?.children || 0}
          </Descriptions.Item>
        </Descriptions>
      </TabPane>
    </Tabs>
  );
};

export default SpaceDetail;
